import { readEmbeddedImageRecord } from './embeddedImageUtils';

export type QuestionImageCorrelation = 'strong' | 'moderate' | 'weak' | 'none';

export type ParticleMultimodalAnalysis = {
  recommended: boolean;
  question_image_correlation: QuestionImageCorrelation;
  reason?: string | null;
  visual_condition_gap_detected?: boolean;
  visual_condition_gap_summary?: string | null;
  missing_condition_warning_detected?: boolean;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function normalizeCorrelation(value: unknown): QuestionImageCorrelation {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'strong' || raw === 'moderate' || raw === 'weak' || raw === 'none') return raw;
  return 'none';
}

/** 从素材 / 颗粒 metadata 读取品控多模态快照 */
export function readMultimodalAnalysisFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): ParticleMultimodalAnalysis | null {
  if (!metadata) return null;
  const qc = asRecord(metadata.quality_control) || asRecord(metadata.qualityControl);
  const raw = asRecord(qc?.multimodal_analysis)
    || asRecord(qc?.multimodalAnalysis)
    || asRecord(metadata.multimodal_analysis)
    || asRecord(metadata.multimodalAnalysis);
  if (!raw) return null;
  return {
    recommended: Boolean(raw.recommended ?? raw.multimodal_analysis_recommended),
    question_image_correlation: normalizeCorrelation(
      raw.question_image_correlation ?? raw.questionImageCorrelation,
    ),
    reason: typeof raw.reason === 'string'
      ? raw.reason
      : (typeof raw.multimodal_analysis_reason === 'string' ? raw.multimodal_analysis_reason : null),
    visual_condition_gap_detected: Boolean(
      raw.visual_condition_gap_detected ?? raw.visualConditionGapDetected,
    ),
    visual_condition_gap_summary: typeof raw.visual_condition_gap_summary === 'string'
      ? raw.visual_condition_gap_summary
      : (typeof raw.visualConditionGapSummary === 'string' ? raw.visualConditionGapSummary : null),
    missing_condition_warning_detected: Boolean(
      raw.missing_condition_warning_detected ?? raw.missingConditionWarningDetected,
    ),
  };
}

/** 与 AppFunctor ttqSourceQaContext.assessStrongMultimodal 对齐 */
export function assessMultimodalBias(input: {
  particleMetadata?: Record<string, unknown> | null;
  hasEmbeddedCrop?: boolean;
  multimodalAnalysis?: ParticleMultimodalAnalysis | null;
}): { strong: boolean; label: string; detail: string } {
  const multimodal = input.multimodalAnalysis
    || readMultimodalAnalysisFromMetadata(input.particleMetadata ?? null);

  if (input.hasEmbeddedCrop || readEmbeddedImageRecord(input.particleMetadata ?? null)) {
    return {
      strong: true,
      label: '多模态偏向：强',
      detail: '含嵌入图，需多模态理解。',
    };
  }
  if (multimodal?.visual_condition_gap_detected) {
    return {
      strong: true,
      label: '多模态偏向：强',
      detail: multimodal.visual_condition_gap_summary?.trim()
        || multimodal.reason?.trim()
        || '题干依赖原图视觉条件。',
    };
  }
  if (multimodal?.recommended && multimodal.question_image_correlation === 'strong') {
    return {
      strong: true,
      label: '多模态偏向：强',
      detail: multimodal.reason?.trim() || '题目与图片强相关。',
    };
  }
  if (multimodal?.recommended && multimodal.missing_condition_warning_detected) {
    return {
      strong: true,
      label: '多模态偏向：强',
      detail: multimodal.reason?.trim() || '文本条件不足，需结合原图。',
    };
  }
  return {
    strong: false,
    label: '多模态偏向：弱',
    detail: multimodal?.reason?.trim() || '纯文本即可，多模态非必须。',
  };
}

export function isStrongMultimodalBias(input: {
  particleMetadata?: Record<string, unknown> | null;
  hasEmbeddedCrop?: boolean;
  multimodalAnalysis?: ParticleMultimodalAnalysis | null;
}): boolean {
  return assessMultimodalBias(input).strong;
}
